import CommercialTasksAPI from '../../api/commercialTasks';

// Agenda de tareas comerciales (E03). La lista de la pestaña activa son uno o
// más grupos que se paginan en orden (Próximas = vencen después de hoy y, al
// final, sin fecha). Completar o cancelar saca la tarea de la lista y descuenta
// los contadores en el momento, sin recargar; después se refrescan los
// contadores en segundo plano para corregir cualquier diferencia.
export const types = {
  SET_UI_FLAG: 'SET_COMMERCIAL_TASKS_UI_FLAG',
  START_LIST: 'START_COMMERCIAL_TASKS_LIST',
  APPEND_PAGE: 'APPEND_COMMERCIAL_TASKS_PAGE',
  SET_SUMMARY: 'SET_COMMERCIAL_TASKS_SUMMARY',
  SET_TIMEZONE: 'SET_COMMERCIAL_TASKS_TIMEZONE',
  UPSERT_TASK: 'UPSERT_COMMERCIAL_TASK',
  REMOVE_TASK: 'REMOVE_COMMERCIAL_TASK',
  DECREMENT_SUMMARY: 'DECREMENT_COMMERCIAL_TASKS_SUMMARY',
};

const SUMMARY_KEYS = ['overdue', 'today', 'upcoming', 'undated', 'unassigned'];
const emptySummary = () =>
  SUMMARY_KEYS.reduce((summary, key) => ({ ...summary, [key]: 0 }), {});

// Vuex 2.1 no acepta `state` como función: el módulo usa un objeto y los
// specs arman uno nuevo con initialState().
export const initialState = () => ({
  // [{ key, params, records, count, page, loaded }]
  groups: [],
  summary: emptySummary(),
  summaryParams: null,
  timezone: null,
  listRequestId: 0,
  summaryRequestId: 0,
  uiFlags: {
    isFetching: false,
    isFetchingMore: false,
    hasError: false,
    isFetchingSummary: false,
    summaryError: false,
  },
});

const isExhausted = group =>
  group.loaded && group.records.length >= group.count;

export const getters = {
  getGroups: $state => $state.groups,
  getRecords: $state => $state.groups.flatMap(group => group.records),
  getSummary: $state => $state.summary,
  getTimezone: $state => $state.timezone,
  getUIFlags: $state => $state.uiFlags,
  hasMore: $state =>
    $state.groups.length > 0 && !$state.groups.every(isExhausted),
  isEmpty: $state =>
    $state.groups.length > 0 &&
    $state.groups.every(group => group.loaded && group.records.length === 0),
};

const errorTask = error =>
  error && error.response && error.response.data
    ? error.response.data.task
    : null;

// Trae la próxima página del primer grupo que no terminó. Si ese grupo se
// termina y el siguiente todavía no cargó, trae también su primera página (así
// "Sin fecha" aparece sin un clic extra cuando hay pocas próximas).
async function loadNext({ state: $state, commit }, requestId) {
  const index = $state.groups.findIndex(group => !isExhausted(group));
  if (index === -1) return;
  const group = $state.groups[index];
  const page = group.loaded ? group.page + 1 : 1;
  const { data } = await CommercialTasksAPI.get({ ...group.params, page });
  if (requestId !== $state.listRequestId) return;
  const meta = data.meta || {};
  commit(types.APPEND_PAGE, {
    index,
    page,
    records: data.payload || [],
    count: meta.count || 0,
  });
  if (meta.timezone) commit(types.SET_TIMEZONE, meta.timezone);
  const next = $state.groups[index + 1];
  if (isExhausted($state.groups[index]) && next && !next.loaded) {
    await loadNext({ state: $state, commit }, requestId);
  }
}

// Una tarea que dejó de estar pendiente: sale de la lista y de los contadores
// en los que estaba (según la versión que se veía antes del cambio).
const dropTerminal = (
  { commit, dispatch, state: $state, rootGetters },
  task
) => {
  commit(types.REMOVE_TASK, task.id);
  if (task.status === 'pending') {
    const scope = $state.summaryParams ? $state.summaryParams.scope : 'all';
    const inScope =
      scope !== 'mine' ||
      task.assigned_user_id === rootGetters.getCurrentUserID;
    commit(types.DECREMENT_SUMMARY, {
      bucket: inScope ? task.due_bucket : null,
      unassigned: !task.assigned_user_id,
    });
  }
  if ($state.summaryParams) {
    dispatch('fetchSummary', { params: $state.summaryParams, silent: true });
  }
};

export const actions = {
  // groups: [{ key, params }] (ver helpers/taskTabs.js)
  fetch: async ({ state: $state, commit }, { groups }) => {
    const requestId = $state.listRequestId + 1;
    commit(types.START_LIST, { groups, requestId });
    commit(types.SET_UI_FLAG, { isFetching: true, hasError: false });
    try {
      await loadNext({ state: $state, commit }, requestId);
      if (requestId === $state.listRequestId) {
        commit(types.SET_UI_FLAG, { isFetching: false });
      }
    } catch (error) {
      if (requestId === $state.listRequestId) {
        commit(types.SET_UI_FLAG, { isFetching: false, hasError: true });
      }
    }
  },

  loadMore: async ({ state: $state, commit }) => {
    if ($state.uiFlags.isFetchingMore || $state.uiFlags.isFetching) return;
    const requestId = $state.listRequestId;
    commit(types.SET_UI_FLAG, { isFetchingMore: true });
    // El error lo muestra la vista; acá solo se libera el flag.
    try {
      await loadNext({ state: $state, commit }, requestId);
    } finally {
      commit(types.SET_UI_FLAG, { isFetchingMore: false });
    }
  },

  // silent: refresco en segundo plano, sin spinner ni error visible.
  fetchSummary: async (
    { state: $state, commit },
    { params, silent = false }
  ) => {
    const requestId = $state.summaryRequestId + 1;
    commit(types.SET_SUMMARY, { requestId, params });
    if (!silent) {
      commit(types.SET_UI_FLAG, {
        isFetchingSummary: true,
        summaryError: false,
      });
    }
    try {
      const { data } = await CommercialTasksAPI.summary(params);
      if (requestId !== $state.summaryRequestId) return;
      commit(types.SET_SUMMARY, { requestId, params, summary: data });
      if (data.timezone) commit(types.SET_TIMEZONE, data.timezone);
      commit(types.SET_UI_FLAG, {
        isFetchingSummary: false,
        summaryError: false,
      });
    } catch (error) {
      if (!silent && requestId === $state.summaryRequestId) {
        commit(types.SET_UI_FLAG, {
          isFetchingSummary: false,
          summaryError: true,
        });
      }
    }
  },

  create: async (_, payload) => {
    const { data } = await CommercialTasksAPI.create(payload);
    return data;
  },

  // 409: la lista pasa a mostrar la versión actual y el error sigue al
  // formulario, que ofrece volver a aplicar los cambios.
  update: async ({ commit }, { id, ...payload }) => {
    try {
      const { data } = await CommercialTasksAPI.update(id, payload);
      commit(types.UPSERT_TASK, data);
      return data;
    } catch (error) {
      const current = errorTask(error);
      if (current) commit(types.UPSERT_TASK, current);
      throw error;
    }
  },

  complete: async (context, task) => {
    try {
      const { data } = await CommercialTasksAPI.complete(
        task.id,
        task.lock_version
      );
      dropTerminal(context, task);
      return data;
    } catch (error) {
      const current = errorTask(error);
      if (current && current.status !== 'pending') dropTerminal(context, task);
      else if (current) context.commit(types.UPSERT_TASK, current);
      throw error;
    }
  },

  cancel: async (context, { task, reason }) => {
    try {
      const { data } = await CommercialTasksAPI.cancel(task.id, {
        lockVersion: task.lock_version,
        reason,
      });
      dropTerminal(context, task);
      return data;
    } catch (error) {
      const current = errorTask(error);
      if (current && current.status !== 'pending') dropTerminal(context, task);
      else if (current) context.commit(types.UPSERT_TASK, current);
      throw error;
    }
  },

  // Pendientes de un contacto (tarjeta del tablero y panel de la conversación),
  // fuera de la lista de la agenda: no toca los grupos de Tareas. Con
  // pipelineId trae las de ese pipeline y las solo de contacto; sin él, todas.
  fetchContactTasks: async ({ commit }, { contactId, pipelineId } = {}) => {
    const { data } = await CommercialTasksAPI.get({
      contact_id: contactId,
      status: 'pending',
      ...(pipelineId
        ? { pipeline_id: pipelineId, include_unpipelined: true }
        : {}),
    });
    const meta = data.meta || {};
    if (meta.timezone) commit(types.SET_TIMEZONE, meta.timezone);
    const tasks = data.payload || [];
    return { tasks, count: meta.count ?? tasks.length };
  },

  fetchEvents: async (_, id) => {
    const { data } = await CommercialTasksAPI.events(id);
    return data.payload || [];
  },
};

export const mutations = {
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.START_LIST]($state, { groups, requestId }) {
    $state.listRequestId = requestId;
    $state.groups = groups.map(group => ({
      key: group.key,
      params: group.params,
      records: [],
      count: 0,
      page: 0,
      loaded: false,
    }));
  },
  [types.APPEND_PAGE]($state, { index, page, records, count }) {
    const group = $state.groups[index];
    if (!group) return;
    // Si la lista cambió entre páginas, una tarea puede repetirse: se ignora.
    const known = new Set(group.records.map(record => record.id));
    const fresh = records.filter(record => !known.has(record.id));
    group.records = [...group.records, ...fresh];
    group.page = page;
    group.loaded = true;
    // Una página vacía cierra el grupo aunque el total diga otra cosa.
    group.count = records.length === 0 ? group.records.length : count;
  },
  [types.SET_SUMMARY]($state, { requestId, params, summary }) {
    $state.summaryRequestId = requestId;
    $state.summaryParams = params;
    if (summary) {
      $state.summary = SUMMARY_KEYS.reduce(
        (result, key) => ({ ...result, [key]: Number(summary[key]) || 0 }),
        {}
      );
    }
  },
  [types.SET_TIMEZONE]($state, timezone) {
    $state.timezone = timezone;
  },
  [types.UPSERT_TASK]($state, task) {
    $state.groups.forEach(group => {
      const index = group.records.findIndex(record => record.id === task.id);
      if (index !== -1) group.records.splice(index, 1, task);
    });
  },
  [types.REMOVE_TASK]($state, id) {
    $state.groups.forEach(group => {
      const index = group.records.findIndex(record => record.id === id);
      if (index !== -1) {
        group.records.splice(index, 1);
        group.count = Math.max(group.count - 1, 0);
      }
    });
  },
  [types.DECREMENT_SUMMARY]($state, { bucket, unassigned }) {
    const summary = { ...$state.summary };
    if (bucket && bucket in summary) {
      summary[bucket] = Math.max(summary[bucket] - 1, 0);
    }
    if (unassigned) summary.unassigned = Math.max(summary.unassigned - 1, 0);
    $state.summary = summary;
  },
};

export default {
  namespaced: true,
  state: initialState(),
  getters,
  actions,
  mutations,
};
